import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { locationsService } from '@/pages/locations/services/locations.service';
import type { LocationShifts, Shift } from '@/pages/locations/types';

// Mock function temporalmente hasta que el endpoint funcione
function getMockLocationShifts(locationId: string | number): LocationShifts {
  const today = new Date().toISOString().split('T')[0];

  // Convertir locationId a número
  let numericId: number;
  if (typeof locationId === 'string') {
    if (locationId.startsWith('loc-')) {
      numericId = parseInt(locationId.replace('loc-', '')) || 1;
    } else {
      numericId = parseInt(locationId) || 1;
    }
  } else {
    numericId = locationId || 1;
  }

  // Simular diferentes configuraciones de turnos según la ubicación
  if (numericId === 1) {
    // Macartur Centro - 2 turnos
    return {
      location_id: numericId,
      date: today,
      is_open: true,
      shifts: [
        {
          type: 'morning',
          name: 'Mañana',
          start_time: '07:00',
          end_time: '15:00'
        },
        {
          type: 'afternoon',
          name: 'Tarde',
          start_time: '15:00',
          end_time: '22:00'
        }
      ],
      shift_type: 'split'
    };
  } else {
    // Otras ubicaciones - turno único
    return {
      location_id: numericId,
      date: today,
      is_open: true,
      shifts: [
        {
          type: 'morning',
          name: 'Mañana',
          start_time: '08:00',
          end_time: '22:00'
        }
      ],
      shift_type: 'single'
    };
  }
}

export interface ActiveShiftInfo {
  shiftName: string;
  startTime: string;
  endTime: string;
  isValidShift: boolean;
  isLocationOpen: boolean;
}

export const useActiveShift = (): ActiveShiftInfo => {
  const { selectedLocation } = useAuth();
  const [shiftInfo, setShiftInfo] = useState<ActiveShiftInfo>({
    shiftName: 'Sin turno',
    startTime: '--:--',
    endTime: '--:--',
    isValidShift: false,
    isLocationOpen: false
  });

  const calculateActiveShift = (locationShifts: LocationShifts): ActiveShiftInfo => {
    const now = new Date();
    const currentTime = now.getHours() * 100 + now.getMinutes(); // Convert to HHMM format

    console.log('🕒 DEBUG calculateActiveShift (location_schedules):', {
      now: now.toISOString(),
      currentTime,
      locationShifts,
      isOpen: locationShifts.is_open,
      shiftType: locationShifts.shift_type,
      shiftsCount: locationShifts.shifts.length
    });

    if (!locationShifts.is_open || locationShifts.shifts.length === 0) {
      return {
        shiftName: 'Local Cerrado',
        startTime: '--:--',
        endTime: '--:--',
        isValidShift: false,
        isLocationOpen: false
      };
    }

    // Helper function to convert time string to number (e.g., "17:01" -> 1701)
    const timeToNumber = (timeStr: string): number => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 100 + minutes;
    };

    // Check each shift to see if we're currently in it
    for (const shift of locationShifts.shifts) {
      const shiftStart = timeToNumber(shift.start_time);
      const shiftEnd = timeToNumber(shift.end_time);

      if (currentTime >= shiftStart && currentTime <= shiftEnd) {
        return {
          shiftName: shift.name,
          startTime: shift.start_time,
          endTime: shift.end_time,
          isValidShift: true,
          isLocationOpen: true
        };
      }
    }

    // If not in any shift, determine if we're between shifts or completely closed
    if (locationShifts.shift_type === 'split' && locationShifts.shifts.length === 2) {
      const morningShift = locationShifts.shifts.find(s => s.type === 'morning');
      const afternoonShift = locationShifts.shifts.find(s => s.type === 'afternoon');

      if (morningShift && afternoonShift) {
        const morningEnd = timeToNumber(morningShift.end_time);
        const afternoonStart = timeToNumber(afternoonShift.start_time);

        if (currentTime > morningEnd && currentTime < afternoonStart) {
          return {
            shiftName: 'Entre Turnos',
            startTime: afternoonShift.start_time,
            endTime: afternoonShift.end_time,
            isValidShift: false,
            isLocationOpen: false
          };
        }
      }
    }

    // Outside all operating hours
    const firstShift = locationShifts.shifts[0];
    const lastShift = locationShifts.shifts[locationShifts.shifts.length - 1];

    return {
      shiftName: 'Local Cerrado',
      startTime: firstShift?.start_time || '--:--',
      endTime: lastShift?.end_time || '--:--',
      isValidShift: false,
      isLocationOpen: false
    };
  };

  const fetchLocationShifts = async () => {
    try {
      console.log('🔍 DEBUG selectedLocation:', selectedLocation);

      if (!selectedLocation?.id) {
        console.log('❌ No selectedLocation.id found');
        return;
      }

      // Convert location ID if needed (e.g., "Macartur 2" -> "2")
      let locationId = selectedLocation.id;
      console.log('🏢 Original locationId:', locationId, 'type:', typeof locationId);

      if (typeof selectedLocation.id === 'string' && !selectedLocation.id.startsWith('loc-')) {
        // Extract number from location name like "Macartur 2" -> "2"
        const match = selectedLocation.name?.match(/\d+/);
        if (match) {
          locationId = match[0];
          console.log('📝 Converted locationId from name:', locationId);
        }
      }

      console.log('🚀 Calling getLocationShifts with:', locationId);

      // Temporalmente usar solo mock data hasta que el endpoint esté funcionando
      const mockShiftsData = getMockLocationShifts(locationId);
      console.log('📊 Using mock shiftsData:', mockShiftsData);

      const shiftsData = mockShiftsData;

      const activeShift = calculateActiveShift(shiftsData);
      console.log('✅ Calculated activeShift:', activeShift);

      setShiftInfo(activeShift);

      console.log('🕒 Active shift calculated (location_schedules):', {
        location: selectedLocation.name,
        locationId,
        shiftsData,
        activeShift
      });
    } catch (error) {
      console.error('❌ Error fetching location shifts:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        selectedLocation
      });
      // Fallback to default closed state
      setShiftInfo({
        shiftName: 'Sin datos',
        startTime: '--:--',
        endTime: '--:--',
        isValidShift: false,
        isLocationOpen: false
      });
    }
  };

  useEffect(() => {
    fetchLocationShifts();

    // Update shift info every minute
    const interval = setInterval(fetchLocationShifts, 60000);

    return () => clearInterval(interval);
  }, [selectedLocation?.id]);

  return shiftInfo;
};